const CONSENT_KEY = 'aniphys_cookie_consent';
const CONSENT_VERSION = 2;
const SCRIPT_ID = 'aniphys-umami';
const SCRIPT_URL = String(import.meta.env.VITE_UMAMI_SCRIPT_URL ?? '');
const SITE_DOMAIN = 'aniphys.ru';
const YANDEX_METRIKA_ID = 111234954;
const YANDEX_SCRIPT_ID = 'aniphys-yandex-metrika';
const YANDEX_SCRIPT_URL = `https://mc.yandex.ru/metrika/tag.js?id=${YANDEX_METRIKA_ID}`;
const ACTIVATION_RECEIPTS_KEY = 'aniphys_activation_receipts_v1';
const ACTIVATION_RECEIPT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const ACTIVATION_RECEIPT_LIMIT = 512;

export type ActivationGoal = 'onboarding_completed';

type ActivationReceipt = {
  key: string;
  recordedAt: number;
};

const memoryReceipts = new Map<string, number>();

type YandexMetrika = ((counterId: number, method: string, ...params: unknown[]) => void) & {
  a?: unknown[][];
  l?: number;
};

declare global {
  interface Window {
    ym?: YandexMetrika;
  }
}

function analyticsAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: boolean; version?: number };
    return parsed?.analytics === true && parsed.version === CONSENT_VERSION;
  } catch {
    return false;
  }
}

function currentReceipts(receipts: ActivationReceipt[], now: number): ActivationReceipt[] {
  return receipts
    .filter((receipt) => (
      typeof receipt?.key === 'string'
      && typeof receipt.recordedAt === 'number'
      && Number.isFinite(receipt.recordedAt)
      && receipt.recordedAt <= now
      && now - receipt.recordedAt <= ACTIVATION_RECEIPT_TTL_MS
    ))
    .sort((a, b) => a.recordedAt - b.recordedAt)
    .slice(-ACTIVATION_RECEIPT_LIMIT);
}

function storedReceipts(now: number): ActivationReceipt[] {
  try {
    const raw = window.localStorage.getItem(ACTIVATION_RECEIPTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? currentReceipts(parsed as ActivationReceipt[], now) : [];
  } catch {
    return [];
  }
}

function pruneMemoryReceipts(now: number): void {
  for (const [key, recordedAt] of memoryReceipts) {
    if (now - recordedAt > ACTIVATION_RECEIPT_TTL_MS) {
      memoryReceipts.delete(key);
    }
  }
  while (memoryReceipts.size > ACTIVATION_RECEIPT_LIMIT) {
    const oldest = memoryReceipts.keys().next().value as string | undefined;
    if (!oldest) break;
    memoryReceipts.delete(oldest);
  }
}

function activationReceiptKey(goal: ActivationGoal, receiptKey: string): string {
  return `${goal}:${receiptKey}`;
}

function hasActivationReceipt(key: string, now: number): boolean {
  pruneMemoryReceipts(now);
  if (memoryReceipts.has(key)) return true;
  return storedReceipts(now).some((receipt) => receipt.key === key);
}

function rememberActivationReceipt(key: string, now: number): void {
  memoryReceipts.delete(key);
  memoryReceipts.set(key, now);
  pruneMemoryReceipts(now);
  try {
    const receipts = storedReceipts(now).filter((receipt) => receipt.key !== key);
    receipts.push({ key, recordedAt: now });
    window.localStorage.setItem(
      ACTIVATION_RECEIPTS_KEY,
      JSON.stringify(currentReceipts(receipts, now)),
    );
  } catch {
    return;
  }
}

function installYandexMetrika(): void {
  if (document.getElementById(YANDEX_SCRIPT_ID)) return;

  if (!window.ym) {
    const queue = ((...args: unknown[]) => {
      queue.a ??= [];
      queue.a.push(args);
    }) as YandexMetrika;
    queue.l = Date.now();
    window.ym = queue;
  }

  const script = document.createElement('script');
  script.id = YANDEX_SCRIPT_ID;
  script.async = true;
  script.src = YANDEX_SCRIPT_URL;
  document.head.appendChild(script);
  window.ym(YANDEX_METRIKA_ID, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
  });
}

export function installAnalytics(): void {
  if (typeof document === 'undefined') return;
  if (!analyticsAllowed()) return;
  if (SCRIPT_URL && !document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.defer = true;
    script.src = SCRIPT_URL;
    script.setAttribute('data-website-id', SITE_DOMAIN);
    script.setAttribute('data-domains', SITE_DOMAIN);
    document.head.appendChild(script);
  }
  installYandexMetrika();
}

export function trackRegistrationSuccess(): void {
  if (!analyticsAllowed()) return;
  window.ym?.(YANDEX_METRIKA_ID, 'reachGoal', 'ym-register');
}

export function trackActivationGoal(goal: ActivationGoal, receiptKey: string): void {
  if (!analyticsAllowed() || !window.ym || !receiptKey) return;
  const now = Date.now();
  const key = activationReceiptKey(goal, receiptKey);
  if (hasActivationReceipt(key, now)) return;
  window.ym(YANDEX_METRIKA_ID, 'reachGoal', goal);
  rememberActivationReceipt(key, now);
}
